import { Injectable } from '@angular/core';
import { CanActivate, Router, UrlTree } from '@angular/router';
import { AuthService } from '../services/auth.service';

/**
 * Keeps already-authenticated users off the pre-auth screens (login, admin login,
 * MFA setup/verification).
 *
 * Those routes sit outside the guarded layouts, so nothing used to check for an
 * existing session: pressing Back, typing /login, or refreshing on /login all
 * rendered the login form for a signed-in user. This guard is the single place
 * that decision now lives.
 */
@Injectable({ providedIn: 'root' })
export class GuestGuard implements CanActivate {
    constructor(
        private auth: AuthService,
        private router: Router
    ) { }

    canActivate(): boolean | UrlTree {
        // Deliberately not AuthService.getToken(): that falls back to temp_token,
        // which is exactly what a mid-MFA user holds while they still need these
        // screens. Only a full token means "already signed in".
        const token = localStorage.getItem('token');

        if (!token) {
            return true;
        }

        if (this.auth.isTokenExpired(token)) {
            // The session is genuinely dead — this, not "the user reached /login",
            // is what justifies dropping the stored auth data.
            this.auth.clearUser();
            return true;
        }

        // Returning a UrlTree lets the router replace the in-flight navigation
        // instead of firing a second one, so back-navigation cannot loop.
        return this.router.parseUrl(this.auth.isAdminUser() ? '/admin/dashboard' : '/dashboard');
    }
}
